import React from 'react';
import './DomainRuleExamples.css';

interface DomainRuleExamplesProps {
  domain: string;
  onUseExample: (example: string) => void;
}

interface RuleExample {
  title: string;
  description: string;
  example: string;
}

const DOMAIN_EXAMPLES: Record<string, RuleExample[]> = {
  ap: [
    {
      title: 'Invoice Approval Matrix',
      description: 'Hierarchical approval requirements based on amount',
      example: `Create comprehensive AP rules with the following approval matrix:
- Under $1,000: Department manager approval
- $1,000-$10,000: Director approval required
- $10,000-$50,000: VP approval required
- Over $50,000: CFO approval required
- Over $100,000: Board approval required

Include three-way matching requirements with 5% or $100 tolerance (whichever is less).
Require PO for all purchases over $500 except recurring services.
Payment terms: Net 30 standard, 2/10 Net 30 for strategic vendors.`
    },
    {
      title: 'Vendor Onboarding Controls',
      description: 'Requirements for new vendor setup',
      example: `Define vendor onboarding rules:
- W-9/W-8 required before first payment
- Credit references for vendors over $50K annual spend
- Insurance certificates for service providers
- OFAC and sanctions screening mandatory
- Dual approval for vendor master file changes
- Annual vendor review for active vendors
- Inactive vendor deactivation after 18 months`
    },
    {
      title: 'Duplicate Payment Prevention',
      description: 'Controls to prevent duplicate disbursements',
      example: `Establish duplicate payment prevention rules:
- System must flag potential duplicates (same vendor, amount, date within 30 days)
- Require manual review for invoice numbers that differ by only 1-2 characters
- Block exact duplicate invoice numbers for same vendor
- Monthly duplicate payment analysis report required
- Recovery process for identified duplicates within 30 days`
    }
  ],
  ar: [
    {
      title: 'Credit Limit Management',
      description: 'Customer credit policies and approval levels',
      example: `Create AR rules for credit management:
- New customers: Credit check required, initial limit $10K
- Credit limits up to $50K: Credit manager approval
- Credit limits $50K-$250K: CFO approval
- Over $250K: Board approval required
- Annual credit review for all customers
- Automatic hold if 60+ days past due
- Credit limit increase requires current payment status`
    },
    {
      title: 'Collection Procedures',
      description: 'Escalation process for overdue accounts',
      example: `Define collection escalation rules:
- Day 1-30: Email reminder from AR team
- Day 31-45: Phone call from AR supervisor
- Day 46-60: Formal demand letter from controller
- Day 61-90: Account on credit hold, CFO involvement
- Day 91+: Send to collections agency or legal
- Write-off approval: Under $1K - Controller, Over $1K - CFO
- Bad debt reserve: 2% of 60+ day balances`
    },
    {
      title: 'Cash Application Rules',
      description: 'How to apply customer payments',
      example: `Establish cash application rules:
- Apply to oldest invoice first unless customer specifies
- Partial payments: Apply to principal before interest
- Unapplied cash: Hold in suspense for max 5 business days
- Short payments under $10 or 1%: Auto write-off
- Overpayments: Apply to account as credit or refund if >$100
- NSF checks: Immediate credit hold and $35 fee`
    }
  ],
  gl: [
    {
      title: 'Journal Entry Controls',
      description: 'Requirements for manual journal entries',
      example: `Define GL journal entry rules:
- All manual entries require supporting documentation
- Entries over $10K require manager approval
- Entries over $50K require controller approval
- Entries over $100K require CFO approval
- No same-person entry and approval (segregation of duties)
- Monthly recurring entries must be reviewed quarterly
- All entries must have clear business purpose description
- Backdated entries require controller approval`
    },
    {
      title: 'Account Reconciliation Requirements',
      description: 'Monthly reconciliation standards',
      example: `Establish account reconciliation rules:
- Balance sheet accounts: Monthly reconciliation required
- P&L accounts: Quarterly analysis required
- Material accounts (>$100K): Independent review required
- Reconciliations due by day 5 of following month
- Reconciling items over 60 days require resolution plan
- All reconciliations must include supporting documentation
- Preparer and reviewer must be different people`
    },
    {
      title: 'Period Close Controls',
      description: 'Month-end close procedures and deadlines',
      example: `Create period close rules:
- Subledger close by day 2
- Journal entries complete by day 3
- Reconciliations complete by day 5
- Financial statements draft by day 7
- Final statements by day 10
- Prior period adjustments require CFO approval
- Reopening closed periods requires controller and CFO approval
- Accruals must be reversed in following period`
    }
  ],
  payroll: [
    {
      title: 'Overtime and Pay Rules',
      description: 'Wage calculation and overtime policies',
      example: `Define payroll calculation rules:
- Regular hours: 40 hours/week maximum for non-exempt
- Overtime: 1.5x rate for hours 41-60
- Double time: 2x rate for hours over 60 or holidays
- Minimum wage: $15/hour or state minimum if higher
- Shift differential: Night shift +10%, Weekend +15%
- On-call pay: $100/day minimum
- Pay periods: Bi-weekly, paid on Friday
- Final pay: Within 72 hours of termination`
    },
    {
      title: 'Tax Withholding Rules',
      description: 'Federal and state tax requirements',
      example: `Establish tax withholding rules:
- Federal withholding per IRS Publication 15 tables
- State withholding per state requirements
- FICA: 6.2% Social Security, 1.45% Medicare
- Additional Medicare: 0.9% over $200K
- W-4 updates processed within 30 days
- Tax deposits: Semi-weekly for liabilities over $50K
- W-2s issued by January 31
- Quarterly 941 filing by month-end following quarter`
    },
    {
      title: 'PTO and Leave Policies',
      description: 'Paid time off accrual and usage rules',
      example: `Create PTO management rules:
- Accrual: 10 days/year (0-2 years), 15 days (3-5 years), 20 days (5+ years)
- Maximum accrual: 1.5x annual allocation
- Sick leave: 5 days/year, no carryover
- Holiday pay: 10 standard holidays per year
- Bereavement: 3 days immediate family, 1 day extended
- Jury duty: Paid leave with documentation
- PTO payout on termination per state law
- Negative PTO balances recovered from final pay`
    }
  ],
  revenue: [
    {
      title: 'Revenue Recognition Criteria',
      description: 'ASC 606 compliance requirements',
      example: `Define revenue recognition rules per ASC 606:
- Contract must be approved and committed by both parties
- Performance obligations must be identified and distinct
- Transaction price must be determinable
- Price must be allocated to performance obligations
- Revenue recognized when control transfers
- Variable consideration: Use expected value or most likely amount
- Significant financing: Adjust if payment terms exceed 12 months
- Contract modifications: Prospective or cumulative catch-up based on criteria`
    },
    {
      title: 'Contract Review Requirements',
      description: 'Review and approval for customer contracts',
      example: `Establish contract review rules:
- All contracts over $10K require legal review
- Non-standard terms require CFO approval
- Multi-year contracts require board approval if >$1M
- Warranties beyond 12 months require special accounting
- Bill-and-hold arrangements require controller approval
- Consignment sales tracked separately
- Related party transactions require audit committee approval
- Contract changes require same level approval as original`
    },
    {
      title: 'Deferred Revenue Management',
      description: 'Rules for unearned revenue',
      example: `Create deferred revenue rules:
- All prepayments recorded as deferred revenue initially
- Monthly recognition based on performance completion
- Straight-line recognition for time-based services
- Milestone recognition for project-based work
- Deferred revenue aging reviewed monthly
- Balances over 12 months reviewed for collectibility
- Refund liability assessed quarterly
- Contract assets vs liabilities presented net by contract`
    }
  ],
  tax: [
    {
      title: 'Sales Tax Compliance',
      description: 'State and local tax requirements',
      example: `Define sales tax compliance rules:
- Nexus determination for all states with >$100K sales or 200 transactions
- Tax rates updated monthly from tax service provider
- Exemption certificates validated before exempt sales
- Monthly filing for liabilities over $1,000
- Quarterly filing for smaller liabilities
- Use tax self-assessed on all purchases
- Audit defense documentation retained 7 years
- Tax automation software reconciled monthly to GL`
    },
    {
      title: 'Income Tax Provisions',
      description: 'Quarterly and annual tax accruals',
      example: `Establish income tax provision rules:
- Quarterly provision within 5 days of quarter-end
- Annual provision with audited financials
- Effective rate reconciliation required
- Uncertain tax positions (FIN 48) evaluated quarterly
- Deferred taxes calculated on all temporary differences
- Valuation allowance assessed each period
- Tax rate changes applied in period of enactment
- Transfer pricing documentation updated annually`
    },
    {
      title: 'Tax Return Preparation',
      description: 'Filing requirements and deadlines',
      example: `Create tax return preparation rules:
- Federal return due 4/15 (3/15 for corps) with extension to 10/15 (9/15)
- State returns per individual state deadlines
- Estimated payments: 25% quarterly for corps, safe harbor for individuals
- Information returns: 1099s by 1/31, 1096 by 2/28
- International forms: 5471, 8865, etc. with return
- Tax provision to return reconciliation required
- All positions must be more-likely-than-not
- Statute of limitations: Keep records 7 years minimum`
    }
  ],
  inventory: [
    {
      title: 'Inventory Valuation Methods',
      description: 'Costing methodology and consistency',
      example: `Define inventory valuation rules:
- Standard costing with quarterly updates
- Variances analyzed monthly and capitalized if material
- Lower of cost or market applied quarterly
- FIFO for all product lines (consistency required)
- Overhead allocation based on machine hours
- Freight-in capitalized, freight-out expensed
- Obsolescence reserve: 50% for 6-12 months old, 100% for >12 months
- Physical count adjustments require manager approval if >$5K`
    },
    {
      title: 'Cycle Count Requirements',
      description: 'Physical inventory verification',
      example: `Establish cycle count rules:
- A items (high value): Count monthly
- B items (medium value): Count quarterly
- C items (low value): Count annually
- Variance tolerance: 1% or $1,000 per SKU
- Variances over tolerance require investigation
- Root cause analysis for variances over $10K
- Blind counts required (no quantity on count sheets)
- Different person must perform recount`
    },
    {
      title: 'Inventory Controls',
      description: 'Safeguarding and movement controls',
      example: `Create inventory control rules:
- All movements require system transaction
- Transfers between locations need approval
- Scrap over $500 requires supervisor approval
- Consignment inventory tracked separately
- Customer-owned inventory segregated
- Access to warehouse restricted to authorized personnel
- Security cameras required in high-value areas
- Monthly roll-forward reconciliation to GL required`
    }
  ],
  treasury: [
    {
      title: 'Cash Management Policies',
      description: 'Daily cash positioning and investment',
      example: `Define cash management rules:
- Daily cash position report by 10 AM
- Target minimum operating cash: $2M or 5 days expenses
- Excess cash swept to money market daily
- Investment policy: Only investment-grade securities
- Maximum maturity: 90 days for operating funds
- Concentration limits: Max 25% with any one institution
- Wire transfers: Dual approval over $100K
- ACH limits: $500K daily, $2M weekly`
    },
    {
      title: 'Banking Controls',
      description: 'Bank account management and security',
      example: `Establish banking control rules:
- New accounts require CFO and CEO approval
- Signatory changes require board resolution
- Positive pay required on all disbursement accounts
- Account reconciliation within 5 days of statement
- Dormant accounts reviewed quarterly for closure
- Electronic banking: Two-factor authentication required
- Daily transaction monitoring for unusual activity
- Annual banking services RFP for accounts over $10M activity`
    },
    {
      title: 'Foreign Exchange Management',
      description: 'FX exposure and hedging policies',
      example: `Create FX management rules:
- Net exposure calculated monthly by currency
- Hedging required for exposures over $1M
- Natural hedging preferred over derivatives
- Forward contracts limited to 12 months
- Hedge effectiveness testing required quarterly
- Mark-to-market valuations monthly
- Hedge documentation per ASC 815 requirements
- Speculative trading prohibited`
    }
  ],
  fixed_assets: [
    {
      title: 'Capitalization Policies',
      description: 'Thresholds and useful lives',
      example: `Define fixed asset capitalization rules:
- Capitalization threshold: $5,000 per item
- Useful lives: Buildings 40 years, Equipment 5-10 years, Computers 3 years
- Software: Capitalize if >$25K and >1 year benefit
- Leasehold improvements: Shorter of lease term or useful life
- Repairs vs improvements: Capitalize if extends life >1 year
- Component accounting for significant parts
- Interest capitalization for construction >6 months
- Asset tagging required for all items over $1,000`
    },
    {
      title: 'Depreciation Methods',
      description: 'Systematic allocation approaches',
      example: `Establish depreciation rules:
- Straight-line method for all assets (consistency)
- Half-year convention in year of acquisition/disposal
- Salvage value: 10% for vehicles, 0% for others
- Depreciation starts when placed in service
- Fully depreciated assets remain on books until disposed
- Impairment testing when indicators present
- Accelerated depreciation for tax only
- Depreciation expense allocated to cost centers monthly`
    },
    {
      title: 'Asset Disposal Controls',
      description: 'Authorization and accounting for disposals',
      example: `Create asset disposal rules:
- Disposal approval: Under $10K - Manager, Over $10K - Director
- Trade-ins require two bids for comparison
- Donations require board approval and 501(c)(3) verification
- Environmental disposal requirements for IT equipment
- Gain/loss on disposal calculated and analyzed
- Asset removal from books within 30 days of disposal
- Annual physical inventory with reconciliation
- Insurance claims filed within 30 days of loss`
    }
  ]
};

const DomainRuleExamples: React.FC<DomainRuleExamplesProps> = ({ domain, onUseExample }) => {
  const examples = DOMAIN_EXAMPLES[domain] || [];

  if (examples.length === 0) {
    return (
      <div className="domain-examples-container">
        <p className="no-examples">No examples available for this domain yet.</p>
      </div>
    );
  }

  return (
    <div className="domain-examples-container">
      <div className="examples-grid">
        {examples.map((example, idx) => (
          <div key={idx} className="example-card">
            <h4>{example.title}</h4>
            <p className="example-description">{example.description}</p>
            <div className="example-preview">
              {example.example.split('\n').slice(0, 4).join('\n')}
              {example.example.split('\n').length > 4 && '...'}
            </div>
            <button
              type="button"
              className="use-example-btn"
              onClick={() => onUseExample(example.example)}
            >
              Use This Example
            </button>
          </div>
        ))}
      </div>
      <div className="examples-note">
        <strong>Note:</strong> These are examples to get you started. Customize the thresholds, 
        limits, and requirements to match your organization's specific needs and compliance requirements.
      </div>
    </div>
  );
};

export default DomainRuleExamples;